import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { IStringLocalizer } from '../../../../utilities/IStringLocalizer';
import {
    DataCollectionAiAgentOperationState,
    DataCollectionAiAgentOperationCode,
    DataCollectionAiAgentOperationType
} from '../../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import {
    getActiveAIOperationFromState,
    parseAiOperationStateValue,
    isAIOperationSuccess,
    isAIOperationDeleteCompleted,
    isAIOperationDeleteInProgress,
    isAIOperationReadonly
} from '../../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { useLocalizer } from './useLocalizer';

/**
 * State and callbacks returned by useAIOperationMonitor hook
 */
export interface AIOperationMonitorState {
    /** Current processing progress (0-100) */
    processingProgress: number;
    /** Current processing stage */
    currentStage: number;
    /** Whether the operation has failed */
    hasFailed: boolean;
    /** Failure message if operation failed */
    failureMessage: string | null;
    /** Whether the operation was terminated by Exchange due to inactivity */
    isTerminated: boolean;
    /** Whether processing is currently active */
    isProcessing: boolean;
    /** Whether AI operation completed successfully (has answers) */
    hasAIAnswers: boolean;
    /** Whether delete-all operation is currently running */
    isDeleteInProgress: boolean;
    /** Whether delete-all operation completed successfully */
    isDeleteCompleted: boolean;
    /** Whether operation is actively processing (in progress) */
    isActivelyProcessing: boolean;
    /** Current agent operation code for detailed progress tracking */
    agentOperationCode: DataCollectionAiAgentOperationCode | null;
    /** Current agent state string with progress details */
    agentState: string | null;
}

/**
 * Callbacks for handling AI operation state changes
 */
export interface AIOperationMonitorCallbacks {
    /** Called when processing completes */
    onProcessingComplete?: (isComplete: boolean) => void;
    /** Called when processing state changes */
    onProcessingStateChange?: (isProcessing: boolean) => void;
    /** Called when delete-all completes */
    onDeleteCompleted?: () => void;
}

/**
 * Custom hook to monitor AI operation state changes from Redux
 * Encapsulates all the logic for tracking AI processing progress, stages, and failure states
 * 
 * @param callbacks - Callbacks for handling state changes
 * @param localizer - Localization function for error messages
 * @returns Current AI operation state and derived values
 */
export const useAIOperationMonitor = (
    callbacks: AIOperationMonitorCallbacks,
    localizer?: IStringLocalizer,
    operationType?: DataCollectionAiAgentOperationType | null
): AIOperationMonitorState => {
    const { onDeleteCompleted, onProcessingComplete, onProcessingStateChange } = callbacks;
    const localize = useLocalizer(localizer);

    // Redux state selectors
    const aiAgentState = useSelector((state: ApplicationState) =>
        state.aiSurveyAgentState
    );
    const activeOperation = getActiveAIOperationFromState(aiAgentState, operationType);

    // Local state
    const [processingProgress, setProcessingProgress] = useState(0);
    const [currentStage, setCurrentStage] = useState(0);
    const [hasFailed, setHasFailed] = useState(false);
    const [failureMessage, setFailureMessage] = useState<string | null>(null);
    const [isTerminated, setIsTerminated] = useState(false);
    const [agentOperationCode, setAgentOperationCode] = useState<DataCollectionAiAgentOperationCode | null>(null);
    const [agentState, setAgentState] = useState<string | null>(null);
    const [previousState, setPreviousState] = useState<DataCollectionAiAgentOperationState | null>(null);

    // Derived state from Redux
    const isProcessing = !!activeOperation && (aiAgentState?.isLoading || aiAgentState?.isPolling || false);

    // Check if AI operation is complete (has answers)
    const hasAIAnswers = isAIOperationSuccess(activeOperation);
    const isDeleteCompleted = isAIOperationDeleteCompleted(activeOperation);
    const isDeleteInProgress = isAIOperationDeleteInProgress(activeOperation);

    // Check if operation is actively processing (not just loaded but actually in progress)
    const isActivelyProcessing = isAIOperationReadonly(activeOperation);

    /**
     * Handle AI operation failure
     */
    const handleFailure = useCallback((message: string) => {
        setHasFailed(true);
        setFailureMessage(message);
        setProcessingProgress(0);
        setCurrentStage(0);

        if (onProcessingStateChange) {
            onProcessingStateChange(false);
        }
    }, [onProcessingStateChange]);

    /**
     * Effect: Monitor AI operation state changes from Redux
     * Updates local UI state based on AI operation progress
     */
    useEffect(() => {
        if (!activeOperation) {
            // No active operation
            setProcessingProgress(0);
            setCurrentStage(0);
            setHasFailed(false);
            setFailureMessage(null);
            setIsTerminated(false);
            setAgentOperationCode(null);
            setAgentState(null);
            setPreviousState(null);
            return;
        }

        // Handle string enum values from JSON serialization
        const enumValue = parseAiOperationStateValue(activeOperation.state);

        if (typeof enumValue !== 'number') {
            return;
        }
        
        // Update agent operation code and state from the operation
        setAgentOperationCode(activeOperation.agentOperationCode ?? null);
        setAgentState(activeOperation.agentState ?? null);

        // Update UI based on operation state
        switch (enumValue) {
            case DataCollectionAiAgentOperationState.Triggered:
                setProcessingProgress(10);
                setCurrentStage(0); // "Initializing AI workflow..."
                setHasFailed(false);
                setIsTerminated(false);
                break;

            case DataCollectionAiAgentOperationState.RequestInProgress:
                // Calculate stage based on operation code
                const opCode = activeOperation.agentOperationCode;
                let stage = 1; // Default: "Request sent to AI agent..."
                let progress = 20;
                
                if (opCode !== null && opCode !== undefined) {
                    // Parse operation code - handle string enum values
                    const opCodeValue = opCode as unknown as string;
                    const parsedOpCode = typeof opCodeValue === 'string'
                        ? DataCollectionAiAgentOperationCode[opCodeValue as keyof typeof DataCollectionAiAgentOperationCode]
                        : opCode;
                    
                    switch (parsedOpCode) {
                        case DataCollectionAiAgentOperationCode.ReadingSurveyQuestions:
                            stage = 2;
                            progress = 25;
                            break;
                        case DataCollectionAiAgentOperationCode.ReadingDocuments:
                            stage = 3;
                            progress = 40;
                            break;
                        case DataCollectionAiAgentOperationCode.ConsultingRegulatoryDatabase:
                            stage = 4;
                            progress = 55;
                            break;
                        case DataCollectionAiAgentOperationCode.GeneratingAnswerSuggestions:
                            stage = 5;
                            progress = 70;
                            break;
                    }
                }
                
                setProcessingProgress(progress);
                setCurrentStage(stage);
                setHasFailed(false);
                setIsTerminated(false);
                break;

            case DataCollectionAiAgentOperationState.ResponseReceived:
                setProcessingProgress(85);
                setCurrentStage(6); // "Response received from AI agent. Processing..."
                setHasFailed(false);
                setIsTerminated(false);
                break;

            case DataCollectionAiAgentOperationState.PreparingGapSummary:
                setProcessingProgress(90);
                setCurrentStage(7); // "Preparing gap summary..."
                setHasFailed(false);
                setIsTerminated(false);
                break;

            case DataCollectionAiAgentOperationState.ResponseProcessed:
                setProcessingProgress(100);
                setCurrentStage(8); // Complete
                setHasFailed(false);
                setIsTerminated(false);

                // Notify parent components
                if (onProcessingStateChange) {
                    onProcessingStateChange(false);
                }
                if (previousState !== null
                    && previousState !== DataCollectionAiAgentOperationState.ResponseProcessed) {
                    onProcessingComplete?.(true);
                }
                break;

            case DataCollectionAiAgentOperationState.DeleteInProgress:
                setProcessingProgress(50);
                setCurrentStage(0);
                setHasFailed(false);
                break;

            case DataCollectionAiAgentOperationState.DeleteCompleted:
                setProcessingProgress(100);
                setCurrentStage(0);
                setHasFailed(false);

                if (onProcessingStateChange) {
                    onProcessingStateChange(false);
                }

                if (previousState !== null
                    && previousState !== DataCollectionAiAgentOperationState.DeleteCompleted) {
                    onDeleteCompleted?.();
                }
                break;

            case DataCollectionAiAgentOperationState.Failed:
                const message = "We encountered an issue while processing your documents. This could be due to unsupported content, file corruption, or a temporary service issue.";
                setIsTerminated(false);
                handleFailure(
                    localize(aiAgentState?.errorMessage || message)
                );
                break;

            case DataCollectionAiAgentOperationState.TerminatedByExchange:
            case DataCollectionAiAgentOperationState.TerminatedByUser:
                setIsTerminated(true);
                handleFailure(
                    localize(
                        enumValue === DataCollectionAiAgentOperationState.TerminatedByUser
                            ? "This AI request was manually terminated by user."
                            : "This AI request was automatically terminated by Exchange due to agent inactivity."
                    )
                );
                break;
        }
    }, [activeOperation, aiAgentState?.errorMessage, localize, onDeleteCompleted, onProcessingComplete, onProcessingStateChange, handleFailure]);

    /**
     * Effect: Track state transitions
     * Updates previousState when the operation state changes
     */
    useEffect(() => {
        if (activeOperation?.state) {
            const enumValue = parseAiOperationStateValue(activeOperation.state);

            if (typeof enumValue === 'number') {
                setPreviousState(enumValue);
            }
        }
    }, [activeOperation?.state]);

    /**
     * Effect: Monitor processing state changes
     * Notifies parent components when processing state changes
     */
    useEffect(() => {
        if (onProcessingStateChange) {
            onProcessingStateChange(isProcessing);
        }
    }, [isProcessing, onProcessingStateChange]);

    return {
        processingProgress,
        currentStage,
        hasFailed,
        failureMessage,
        isTerminated,
        isProcessing,
        hasAIAnswers,
        isDeleteCompleted,
        isDeleteInProgress,
        isActivelyProcessing,
        agentOperationCode,
        agentState
    };
};

